/**
 * ProviderParallelSet — named list of provider keys that all run in parallel
 * for a single tier slot. The collapse stage picks one winner per the set's
 * `selection` strategy.
 *
 * Stored as a singleton document `provider-parallel-sets` in the `settings`
 * container, sibling to `provider-tiered-sets`. Referenced from a `TierSlot`
 * with `kind: 'parallel'` — never directly from a pipeline cell.
 *
 * Each branch names a provider and may carry model-shaped params for that
 * branch only (thinking budget, reasoning effort, …), overriding the provider
 * registry default. Task-shaped params (temperature, maxTokens, timeoutMs) come
 * from the pipeline runstep and apply to every branch. See
 * docs/FeatureDesigns/PipelineInfra/provider-and-parameter-management.md §3.
 *
 * See docs/FeatureDesigns/PipelineInfra/analysis-routing.md (§3.3, §4.3) for the design.
 */

import { normalizeSlotParams, type SlotParams } from './providerConfigTypes';

/**
 * Selection strategy for collapsing N parallel branch outcomes to one:
 *   - 'default'     — pick the branch marked `defaultBranch`, with parse /
 *                     call error fallback to any successful branch.
 *   - 'best-result' — deferred. Validator rejects this at save time in v1 and
 *                     unlocks behind a scorer design in a follow-up.
 */
export type ParallelSelection = 'default' | 'best-result';

/**
 * One branch of a parallel set: a provider key, an optional label, and optional
 * model-shaped params for that branch only.
 *
 * Params sit per branch rather than on the tier slot that references the set,
 * because branches can span families — one value on the slot would be legal on
 * some branches and meaningless on others, the same problem that keeps these
 * params off the runstep.
 *
 * `label` identifies the branch when the provider key cannot: two branches on
 * the SAME model differing only by params is a legitimate comparison ("does a
 * bigger thinking budget help?"), and it was previously impossible — the only
 * way to express it was to mint a second provider entry pointing at the same
 * model, which is how `gemini-25-flash-hightoken` and `gpt-mini-minimal` came
 * to exist. Absent, it defaults to the provider key, so every set written
 * before labels existed behaves and fingerprints identically.
 */
export interface ParallelBranch {
    provider: string;
    /** Branch identity. Defaults to `provider`; set it only to disambiguate two
     *  branches sharing a provider. `defaultBranch` references this. */
    label?: string;
    params?: SlotParams;
}

/** How a branch is identified: its label, or its provider key when unlabelled.
 *  The single definition of branch identity — dedupe, `defaultBranch`
 *  resolution and trace tags all key off this. */
export function branchKey(b: ParallelBranch): string {
    return b.label ?? b.provider;
}

/**
 * Read a persisted `branches` array, accepting both the original `string[]`
 * and the `{ provider, params? }[]` form.
 *
 * The string form is not legacy debt to migrate away from — it stays readable
 * indefinitely, because every existing parallel set is stored that way and a
 * set with no per-branch params has nothing to gain from the object form. This
 * is the one non-additive shape change in the params design, so the compat path
 * is load-bearing rather than a courtesy.
 */
export function normalizeParallelBranches(raw: unknown): ParallelBranch[] {
    if (!Array.isArray(raw)) return [];
    const out: ParallelBranch[] = [];
    for (const b of raw) {
        if (typeof b === 'string') {
            out.push({ provider: b });
            continue;
        }
        if (b && typeof b === 'object') {
            const o = b as Record<string, unknown>;
            if (typeof o.provider !== 'string') continue;
            const params = normalizeSlotParams(o.params);
            // A label equal to the provider key is the default, so drop it —
            // otherwise the same branch fingerprints two ways depending on
            // whether the writer spelled the default out.
            const label = typeof o.label === 'string' && o.label && o.label !== o.provider
                ? o.label : undefined;
            out.push({
                provider: o.provider,
                ...(label ? { label } : {}),
                ...(params ? { params } : {}),
            });
        }
    }
    return out;
}

/** Provider keys of a branch list, for call sites that resolve providers
 *  (capability checks, registry lookups). Use {@link branchKey} for identity —
 *  these are not the same once two branches share a provider. */
export function branchProviderKeys(branches: ParallelBranch[]): string[] {
    return branches.map(b => b.provider);
}

/** Branch identities of a list — dedupe and `defaultBranch` resolution. */
export function branchKeys(branches: ParallelBranch[]): string[] {
    return branches.map(branchKey);
}

export interface ProviderParallelSet {
    /** Admin-chosen identifier, e.g. 'frontier-3'. Lowercase, dash-separated.
     *  Namespace is independent of `ProviderTieredSet.name`. */
    name: string;

    /** Admin-facing description shown in the Parallel Sets UI. */
    description?: string;

    /** 2–5 branches. A single-branch parallel set is degenerate — use a
     *  `kind: 'single'` tier slot instead. Branch ids in traces are the
     *  provider keys themselves.
     *
     *  Persisted as either a bare provider key (the original shape) or
     *  `{ provider, params? }`. `normalizeParallelBranches` collapses both to
     *  the object form on read — see its note on why the string form must keep
     *  working. */
    branches: ParallelBranch[];

    /** Branch that wins when `selection === 'default'` and no parse / call error
     *  forces a fallback. Matched against `branchKey` — the branch's label, or
     *  its provider key when unlabelled — so it stays a plain provider key for
     *  every set that has no duplicate providers.
     *
     *  Set this to the INCUMBENT: the branch's output is the one used and the
     *  rest are recorded, so pointing it at whatever would have run anyway makes
     *  enabling a comparison cost N× without changing a single answer. */
    defaultBranch: string;

    selection: ParallelSelection;
}

/**
 * Cosmos doc id is `provider-parallel-sets` in the `settings` container.
 */
export interface ProviderParallelSetsDoc {
    id: 'provider-parallel-sets';
    type: 'provider-parallel-sets';
    sets: ProviderParallelSet[];
    /** Written by the CRUD handlers. Cosmos `_ts` is authoritative for
     *  last-modified ordering. */
    updatedBy?: string;
}
